import { skipToken, useQuery } from '@tanstack/react-query';
import type React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { Tooltip } from '@gitbook/spine-react';

import { apiQuery } from '../../state';
// Deep imports: the ChangeRequest barrel reaches back into AgentChat, so going through it would
// close a cycle. Neither module imports anything of the application's own.
import {
    ChangeRequestStatusIcon,
    getChangeRequestStatusLabel,
} from '../ChangeRequest/ChangeRequestStatus';
import { getChangeRequestSubject } from '../ChangeRequest/revisionTitles';
import type { AgentEditTarget } from './editTarget';

/**
 * How a target is named on screen.
 */
export type EditTargetDetails = {
    /**
     * Rendered rather than handed over as a component: a change request's icon is the one for its
     * status, in that status's colour and with the status named on hover, which the caller has no
     * way to add on.
     */
    icon: React.ReactNode;
    /** The change request's subject, or the space's title. */
    label: string;
    /** The change request's number, shown as "#42". Absent for a space, and until it resolves. */
    number: number | undefined;
    /** What the target is called in a sentence, e.g. "Open change request". */
    noun: 'change request' | 'space';
};

/**
 * Resolve what a target is called.
 *
 * The name is read here rather than carried around: what the caller has is a change request's number
 * or id, or a space's id, and none of them says anything about what the user is working on.
 */
export function useEditTargetDetails(target: AgentEditTarget): EditTargetDetails;
export function useEditTargetDetails(target: AgentEditTarget | null): EditTargetDetails | null;
export function useEditTargetDetails(target: AgentEditTarget | null): EditTargetDetails | null {
    const { data: changeRequest } = useQuery({
        ...apiQuery(
            'getChangeRequestById',
            target?.kind === 'change_request'
                ? // The path parameter is a string, and the API resolves an id or a number given as
                  // one — which is what this endpoint is addressed by everywhere in the product.
                  { spaceId: target.spaceId, changeRequestId: String(target.changeRequest) }
                : skipToken
        ),
        // The target is worth naming the moment it is known; the subject fills in when it arrives,
        // rather than the whole row waiting on a request.
        retry: false,
        throwOnError: false,
    });
    const { data: space } = useQuery({
        // On the same key the space screen fills, so the title is already there. Deliberately
        // without `nullOnError`, which `getAPIQueryKey` folds into the query key: with it this would
        // sit on a key of its own and cost a second request for a space already in the cache. A
        // lookup that fails leaves the title blank rather than throwing out of the dock.
        ...apiQuery(
            'getSpaceById',
            target?.kind === 'space' ? { spaceId: target.spaceId } : skipToken
        ),
        retry: false,
        throwOnError: false,
    });

    if (!target) {
        return null;
    }

    if (target.kind === 'space') {
        return {
            icon: <Icon.Space className="size-3.5 shrink-0 text-neutral-high" />,
            label: space?.title?.trim() || 'Untitled space',
            number: undefined,
            noun: 'space',
        };
    }

    const subject = changeRequest ? getChangeRequestSubject(changeRequest) : undefined;

    return {
        // The status's own icon and colour once the lookup lands — the same ones the rest of the
        // application marks the change request with — and the plain icon until then. Wrapped in a
        // span for the tooltip to hold on to, since the icon itself takes no ref.
        icon: changeRequest ? (
            <Tooltip label={getChangeRequestStatusLabel(changeRequest.status)}>
                <span className="flex shrink-0 cursor-help">
                    <ChangeRequestStatusIcon
                        status={changeRequest.status}
                        colored
                        className="size-3.5"
                    />
                </span>
            </Tooltip>
        ) : (
            <Icon.ChangeRequest className="size-3.5 shrink-0 text-neutral-high" />
        ),
        // The name the rest of the application shows for it, which for a change request nobody has
        // titled is the one it was given on creation rather than "untitled".
        label: subject?.trim() || 'Untitled change request',
        // Shown as "#42": the number the caller carried, or the one the lookup resolved where it
        // carried an id instead.
        number:
            typeof target.changeRequest === 'number' ? target.changeRequest : changeRequest?.number,
        noun: 'change request',
    };
}
